import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ForbiddenComponent from "src/core/error/forbidden.component";
import BreadcrumbComponent from "../../core/components/breadcrumb/breadcrumb.component";
import ContentHeader from "../../shared/content-header/content-header.component";
import { BreadCrumbInfoModel } from "../../shared/models/breadCrumbInfo.model";
import SideBarComponent from "../../shared/sidebar/sidebar.component";

const AdminComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const modules = useSelector((state: any) => state.navbar.modules);

  const [firstAccessibleRoute, setFirstAccessibleRoute] = useState<
    string | null
  >(null);
  const [hasAccessibleRoute, setHasAccessibleRoute] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(true);

  // Find the first accessible leaf route under /admin
  const findFirstAccessibleAdminRoute = (modules: any[]): string | null => {
    const adminModule = modules.find((m: any) => m.name === "Admin");
    if (!adminModule) return null;

    // Recursively collect leaf URLs that are accessible
    const collectAccessibleLeafUrls = (
      menu: any,
      currentPath: string = "",
    ): string[] => {
      let urls: string[] = [];
      let hasAccessibleChild = false;

      if (menu.childMenuList?.length) {
        const childPath = currentPath + (menu.url || "");
        for (const child of menu.childMenuList) {
          const childUrls = collectAccessibleLeafUrls(child, childPath);
          if (childUrls.length > 0) {
            hasAccessibleChild = true;
            urls.push(...childUrls);
          }
        }
      }

      // Only add this menu if it has no accessible children AND it itself is accessible
      if (
        !hasAccessibleChild &&
        menu.isActiveChild &&
        menu.hasPermission &&
        menu.url
      ) {
        urls.push(currentPath + menu.url);
      }

      return urls;
    };

    const accessibleUrls: string[] = [];
    for (const child of adminModule.childMenuList) {
      accessibleUrls.push(...collectAccessibleLeafUrls(child, ""));
    }

    if (accessibleUrls.length > 0) {
      return `/admin${accessibleUrls[0]}`;
    }

    return null;
  };

  // Determine accessible routes when modules load
  useEffect(() => {
    if (modules?.length) {
      const route = findFirstAccessibleAdminRoute(modules);

      setFirstAccessibleRoute(route);
      setHasAccessibleRoute(!!route);
      setIsLoading(false);
    }
  }, [modules]);

  // Redirect from /admin to the first accessible route
  useEffect(() => {
    if (location?.pathname === "/admin" && firstAccessibleRoute) {
      navigate(firstAccessibleRoute);
    }
  }, [location?.pathname, firstAccessibleRoute, navigate]);

  // const userListRestrictedToUserIds = ["3", "6", "7"];

  const baseBreadCrumbInfo = useMemo(
    () => ({
      label: "Admin",
      active: false,
      onClick: () => firstAccessibleRoute && navigate(firstAccessibleRoute),
    }),
    [firstAccessibleRoute],
  );

  const [updatedBreadCrumbInfo, setUpdatedBreadCrumbInfo] = useState<
    BreadCrumbInfoModel[]
  >([baseBreadCrumbInfo]);

  useEffect(() => {
    const { pathname } = location;
    let breadCrumbInfo: BreadCrumbInfoModel[] = [baseBreadCrumbInfo];

    const routeSlice = pathname.slice(
      pathname.indexOf("/admin") + "/admin".length,
    );

    if (routeSlice.includes("user")) {
      breadCrumbInfo = [
        baseBreadCrumbInfo,
        {
          label: "User",
          active: false,
          onClick: () => navigate("/admin/user/userList"),
        },
      ];

      if (routeSlice.includes("user/userList")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "User List",
            active: true,
          },
        ];
      } else if (routeSlice.includes("user/pendingapproval")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Pending Approval",
            active: true,
          },
        ];
      } else if (routeSlice.includes("user/role")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Role",
            active: true,
          },
        ];
      } else if (routeSlice.includes("user/accessmapping")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Access Mapping",
            active: true,
          },
        ];
      }
    } else if (routeSlice.includes("smarlightsupplier")) {
      breadCrumbInfo = [
        baseBreadCrumbInfo,
        {
          label: "Smart Light Supplier",
          active: false,
          onClick: () => navigate("/admin/smarlightsupplier/supplierlist"),
        },
      ];

      if (routeSlice.includes("smarlightsupplier/supplierlist")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Supplier List",
            active: true,
          },
        ];
      }
    } else if (routeSlice.includes("location")) {
      breadCrumbInfo = [
        baseBreadCrumbInfo,
        {
          label: "Location",
          active: false,
          onClick: () => navigate("/admin/location/towncouncil"),
        },
      ];

      if (routeSlice.includes("location/towncouncil")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Town Council",
            active: true,
          },
        ];
      } else if (routeSlice.includes("location/site")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Site",
            active: true,
          },
        ];
      } else if (routeSlice.includes("location/block")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Block",
            active: true,
          },
        ];
      }
    } else if (routeSlice.includes("inventory")) {
      breadCrumbInfo = [
        baseBreadCrumbInfo,
        {
          label: "Inventory",
          active: false,
          onClick: () => navigate("/admin/inventory/assetmodel"),
        },
      ];

      if (routeSlice.includes("inventory/assetmodel")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Asset Model",
            active: true,
          },
        ];
      } else if (routeSlice.includes("inventory/drivermodel")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Driver Model",
            active: true,
          },
        ];
      } else if (routeSlice.includes("inventory/asset")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Asset",
            active: true,
          },
        ];
      }
    } else if (routeSlice.includes("dms")) {
      breadCrumbInfo = [
        baseBreadCrumbInfo,
        {
          label: "Device Management System",
          active: false,
          onClick: () => navigate("/admin/dms/dashboard"),
        },
      ];

      if (routeSlice.includes("dms/dashboard")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "DMS Overview",
            active: true,
          },
        ];
      } else if (routeSlice.includes("dms/device")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Device",
            active: true,
          },
        ];
      } else if (routeSlice.includes("dms/certificate")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Certificate",
            active: true,
          },
        ];
      } else if (routeSlice.includes("dms/generate-certificate")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Generate Certificate",
            active: true,
          },
        ];
      } else if (routeSlice.includes("dms/ota")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "OTA",
            active: true,
          },
        ];
      } else if (routeSlice.includes("dms/sim")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "SIM Card",
            active: true,
          },
        ];
      }
    } else if (routeSlice.includes("configuration")) {
      breadCrumbInfo = [
        baseBreadCrumbInfo,
        {
          label: "Configuration",
          active: false,
          onClick: () => navigate("/admin/configuration/baseline"),
        },
      ];

      if (routeSlice.includes("configuration/baseline")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Baseline",
            active: true,
          },
        ];
      } else if (routeSlice.includes("configuration/alertconfiguration")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Alert Configuration",
            active: true,
          },
        ];
      } else if (routeSlice.includes("configuration/reportconfiguration")) {
        breadCrumbInfo = [
          ...breadCrumbInfo,
          {
            label: "Report Configuration",
            active: true,
          },
        ];
      }
    }

    setUpdatedBreadCrumbInfo(breadCrumbInfo);
  }, [location, baseBreadCrumbInfo]);

  useEffect(() => {
    if (isLoading) {
      document.body.classList.add("loading-indicator");
    } else {
      document.body.classList.remove("loading-indicator");
    }

    return () => {
      document.body.classList.remove("loading-indicator");
    };
  }, [isLoading]);

  // Show loading while checking permissions
  if (isLoading) {
    return <div className="loading-spinner"></div>;
  }

  // No accessible route → user has no permission for any admin section
  if (hasAccessibleRoute === false) {
    return <ForbiddenComponent />;
  }

  return (
    <>
      <Helmet>
        <title>Admin | SLP</title>
      </Helmet>
      <div>
        <div className="header-container">
          <div className="title-container">
            <ContentHeader
              pageTitle={
                updatedBreadCrumbInfo[updatedBreadCrumbInfo?.length - 1]?.label
              }
            />
          </div>
        </div>

        <div className="page">
          <div className="page-container mt-4">
            <BreadcrumbComponent breadCrumbInfo={updatedBreadCrumbInfo} />
            <div className="flex justify-between">
              <div className="w-lg-25">
                <SideBarComponent moduleName={"Admin"} isAccordion={true} />
              </div>
              <div
                className="w-lg-75 bg-card"
                style={{ marginLeft: "1rem", zIndex: 999 }}
              >
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminComponent;
